import React from 'react';
import { observer } from 'mobx-react';

import { useAppManager } from 'core/hooks';
import { analytics } from 'core/analytics';
import { routerStore } from 'core/routing';

import { frameStore } from 'apps/components/Frame/appManager';
import { clientStore } from 'apps/client/app';

import {
  ConversionManager,
  CAD_TASK_STATUS,
} from 'apps/conversions/appManager';
import { getFormatObject } from 'libs/rendering/utils';

import { Spinner, Icon } from 'components';

import { themes } from 'styles/themes/wikifactoryNew';
import {
  StyledAccordion,
  StyledAccordionItem,
  StyledAccordionPanel,
  DownloadTextMain,
  DownloadTextSecondary,
  StyledAccordionButton,
  DownloadIcon,
  DownloadEntryRow,
} from './downloadButton.styled';

interface DownloadCadConversionFormatButtonProps {
  file: { id: string; permalink?: string };
  format: string;
  originalExt: string;
}

const DownloadCadConversionFormatButton = observer(
  (props: DownloadCadConversionFormatButtonProps) => {
    const { file, format, originalExt } = props;

    let statusIcon = <Icon name='download' />;

    const fileId = file.id;
    const contextId = frameStore.contextManager?.context?.id;
    const operation = useAppManager(
      ConversionManager,
      null,
      { fileId, format, contextId },
      true,
      [fileId, contextId],
    );

    const conversionStatus = operation?.status;
    if (conversionStatus === CAD_TASK_STATUS.CONVERTING)
      statusIcon = <Spinner size='small' isDark />;
    else if (conversionStatus === CAD_TASK_STATUS.FAILED)
      statusIcon = <Icon name='error' />;
    // else if (conversionStatus === CAD_TASK_STATUS.FINISHED)
    //   statusIcon = <Icon name='download' />;

    let text = `Download as .${format.toLowerCase()}`;
    let secondaryText = '';
    if (format === originalExt) {
      secondaryText = '(Original file)';
    } else if (operation && operation.statusText !== '') {
      text = operation.statusText;
    }

    const downloadEntry = (
      <DownloadEntryRow
        style={{
          background: themes.color.wfWhite,
          color: themes.color.wfBlack,
          boxShadow: '0px 2px 2px gray',
        }}
      >
        <DownloadTextMain>{text}</DownloadTextMain>
        <DownloadTextSecondary>{secondaryText}</DownloadTextSecondary>
        {statusIcon}
      </DownloadEntryRow>
    );

    // - need to make sure we don't request a conversion for the (original) originalExt,
    // so this returns either a dumb download button that just uses the permalink,
    // or a button that triggers a new conversion
    if (format === originalExt) {
      return (
        <DownloadIcon
          onClick={() => {
            analytics.sendEvent('viewer-downloaded-project');
          }}
          href={file?.permalink}
          download
          target='_blank'
          rel='noreferrer'
          data-testid='cadRoom-download'
          key={`download-orignal-${originalExt}`}
        >
          {downloadEntry}
        </DownloadIcon>
      );
    }
    return (
      <DownloadIcon
        onClick={() => {
          analytics.sendEvent('viewer-downloaded-project');
          operation.StartCadConversion();
        }}
        download
        target='_blank'
        rel='noreferrer'
        key={`download-conversion-${format}`}
      >
        {downloadEntry}
      </DownloadIcon>
    );
  },
);

export const DownloadButton = observer(() => {
  const { route } = routerStore;
  const { file, sharedFileManager, project } = frameStore;
  const { isRestricted } = sharedFileManager?.sharedFileQuery?.result || {};
  const isIntermediator = clientStore.hasRole('INTERMEDIATOR');
  // if current user is intermediator, bypass the condition
  const shouldShowButton =
    isIntermediator || (!project?.isRestricted && !isRestricted);

  if (!file) return null;
  if (project && !project?.canDownload) return null;
  if (!clientStore.signedInProfile) return null;
  if (route.name === 'workspace.share-file') return null;

  const { formatObject, extension: originalExt } = getFormatObject(file);
  const pathParts = file.filename.split('.');
  if (pathParts.length < 2) {
    return null;
  }
  const formatsWithoutConversions = [
    'pdf',
    'jpg',
    'png',
    'txt',
    '.md',
    '.svg',
    '.sql',
  ];

  // - if this format is for the GltfViewer, but its gltf conversion
  // failed, then converting it to stl will likely fail too, so we
  // push the format into formatsWithoutConversions so that we
  // only add the download for the original file into the dropdown
  // but no other conversions
  if (
    formatObject.viewer === 'GltfViewer' &&
    file.conversionGltfjson?.status !== 'SUCCESS'
  ) {
    formatsWithoutConversions.push(originalExt);
  }

  // - if this is a format that is not for the GltfViewer at all only
  // allow download of original file but no conversions (same as above)
  if (formatObject.viewer !== 'GltfViewer') {
    formatsWithoutConversions.push(originalExt);
  }

  const downloadFormats = [];
  downloadFormats.push(originalExt);
  if (!formatsWithoutConversions.includes(originalExt)) {
    if (originalExt !== 'stl') {
      downloadFormats.push('stl');
    }
    // check if this is a file format for which we want
    // to disable step conversions
    const disableStepFormats = ['stl', 'dxf', 'dwg', 'step', 'stp'];
    if (!disableStepFormats.includes(originalExt)) {
      downloadFormats.push('step');
    }
  }

  const downloadButtons = downloadFormats.map(downloadExt => {
    return (
      <DownloadCadConversionFormatButton
        key={`download-format-button-${downloadExt}`}
        file={file}
        format={downloadExt}
        originalExt={originalExt}
      />
    );
  });

  const hoverStyle: React.CSSProperties = {
    background: 'none',
    opacity: 0.8,
  };

  return (
    shouldShowButton && (
      <div>
        <StyledAccordion
          data-testid='shareFilePage-download-wrapper'
          allowToggle
          _hover={{
            background: 'none',
            opacity: 1,
          }}
        >
          <StyledAccordionItem>
            <StyledAccordionButton _hover={hoverStyle} fontSize='0.875rem'>
              Download
              <Icon
                style={{ marginLeft: '0.5em', marginRight: '0.5em' }}
                name='download'
                size='default'
              />
              <Icon name='caret_down' size='0.75em' />
            </StyledAccordionButton>

            <StyledAccordionPanel>{downloadButtons}</StyledAccordionPanel>
          </StyledAccordionItem>
        </StyledAccordion>
      </div>
    )
  );
});
